import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LabelsService from "../../../services/LabelsService";
import { BRAND_COLOR, USAGE_TYPES, getErrorMessage, getUsageTotal } from "./labelUtils";

const USER_USAGE_HINTS = {
  usersWithProfile: (count) => `Se quitará como perfil de ${count} usuario${count === 1 ? "" : "s"}.`,
  userInterests: (count) => `Se quitará de los intereses de ${count} usuario${count === 1 ? "" : "s"}.`,
};

// Tarjetas propias en lugar de Accordion: globals.css estiliza .MuiAccordionSummary-root para otras vistas
const UsageSection = ({ Icon, title, count, refs, hint }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "grey.200",
      borderRadius: 2,
      bgcolor: "background.paper",
      overflow: "hidden",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.25, bgcolor: "grey.50" }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(BRAND_COLOR, 0.12),
          color: BRAND_COLOR,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography sx={{ flexGrow: 1, fontWeight: 600, fontSize: 14.5, color: "text.primary" }}>
        {title}
      </Typography>
      <Box
        sx={{
          minWidth: 28,
          height: 24,
          px: 1,
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND_COLOR,
          color: "#fff",
          fontSize: 12.5,
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
    </Stack>

    <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "grey.200" }}>
      {refs.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ maxHeight: 132, overflowY: "auto" }}>
          {refs.map((ref) => (
            <Chip
              key={ref.id}
              label={ref.name || `#${ref.id}`}
              size="small"
              title={ref.name || `#${ref.id}`}
              sx={{
                maxWidth: "100%",
                bgcolor: "grey.100",
                color: "text.primary",
                fontWeight: 500,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            />
          ))}
          {count > refs.length && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", ml: 0.5 }}>
              y {count - refs.length} más...
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  </Box>
);

const DeleteLabelDialog = ({ open, label, onClose, onDeleted }) => {
  const [usageDetail, setUsageDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !label) return;

    let cancelled = false;
    setUsageDetail(null);
    setConfirmUnlink(false);
    setError("");
    setIsLoading(true);

    LabelsService.getLabelUsage(label.id)
      .then((data) => {
        if (!cancelled) setUsageDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, "No se pudo verificar el uso de la etiqueta."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, label]);

  const usage = usageDetail?.usage;
  const total = getUsageTotal(usage);
  const isInUse = total > 0;
  const usedTypes = USAGE_TYPES.filter(({ key }) => (usage?.[key] || 0) > 0);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError("");
      await LabelsService.deleteLabel(label.id, isInUse);
      onDeleted?.(`Etiqueta "${label.name}" eliminada correctamente.`);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.usage) {
        setUsageDetail(err.response.data.usage);
        setConfirmUnlink(false);
        setError("La etiqueta empezó a usarse en otros registros. Revisa el detalle antes de continuar.");
      } else {
        setError(getErrorMessage(err, "No se pudo eliminar la etiqueta."));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
              color: "error.main",
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 20 }}>
            Eliminar etiqueta
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2.5, mt: 0.5 }}>
          <Typography color="text.secondary">¿Deseas eliminar la etiqueta</Typography>
          <Chip
            icon={<LocalOfferOutlinedIcon sx={{ fontSize: 16 }} />}
            label={label?.name}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: alpha(BRAND_COLOR, 0.12),
              color: BRAND_COLOR,
              "& .MuiChip-icon": { color: BRAND_COLOR },
            }}
          />
          <Typography color="text.secondary">?</Typography>
        </Stack>

        {isLoading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">Verificando dónde se usa esta etiqueta...</Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && usageDetail && !isInUse && (
          <Alert icon={<CheckCircleOutlineIcon />} severity="success">
            Esta etiqueta no está asociada a ningún registro. Puedes eliminarla con seguridad.
          </Alert>
        )}

        {!isLoading && isInUse && (
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Esta etiqueta está asociada a <strong>{total}</strong> registro{total === 1 ? "" : "s"}. Si la
              eliminas, se desvinculará automáticamente de todos ellos.
            </Alert>

            <Box>
              <Typography
                variant="overline"
                sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.6, display: "block", mb: 1 }}
              >
                Dónde se usa
              </Typography>
              <Stack spacing={1.25}>
                {usedTypes.map(({ key, refsKey, label: typeLabel, Icon }) => (
                  <UsageSection
                    key={key}
                    Icon={Icon}
                    title={typeLabel}
                    count={usage[key]}
                    refs={refsKey ? usageDetail[refsKey] || [] : []}
                    hint={USER_USAGE_HINTS[key]?.(usage[key]) ?? ""}
                  />
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: confirmUnlink ? "error.light" : "grey.200",
                bgcolor: (theme) => (confirmUnlink ? alpha(theme.palette.error.main, 0.05) : "transparent"),
                transition: "all .2s",
              }}
            >
              <FormControlLabel
                sx={{ m: 0, width: "100%" }}
                control={
                  <Checkbox
                    checked={confirmUnlink}
                    onChange={(event) => setConfirmUnlink(event.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    Entiendo que la etiqueta se quitará de todos estos registros.
                  </Typography>
                }
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={isDeleting} color="inherit" sx={{ textTransform: "none", fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isLoading || isDeleting || !usageDetail || (isInUse && !confirmUnlink)}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5 }}
        >
          {isInUse ? "Desvincular y eliminar" : "Eliminar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteLabelDialog;
